import { Button, Input, Label, TextArea, TextField } from "@heroui/react";
import {
	type AdminSeriesBible,
	NARRATIVE_MODE_VALUES,
	NarrativeMode,
	type UpsertSeriesBibleInput,
	VISUAL_STYLE_VALUES,
	VisualStyle,
} from "@storyline/shared";
import { useState } from "react";
import Pill from "@/src/common/components/pill";
import SelectField from "@/src/common/components/select-field";
import { NARRATIVE_MODE_OPTIONS, VISUAL_STYLE_OPTIONS } from "@/src/common/constants/studio-labels";
import { narrowEnum } from "@/src/common/utils/narrow-enum";

type BibleFormProps = {
	bible: AdminSeriesBible | null;
	onSave: (body: UpsertSeriesBibleInput) => Promise<void>;
	onToggleLock: (locked: boolean) => Promise<void>;
};

const BibleForm: React.FC<BibleFormProps> = ({ bible, onSave, onToggleLock }) => {
	const locked = bible?.locked ?? false;
	const [visualStyle, setVisualStyle] = useState<VisualStyle>(bible?.visualStyle ?? VisualStyle.CINEMATIC);
	const [narrativeMode, setNarrativeMode] = useState<NarrativeMode>(bible?.narrativeMode ?? NarrativeMode.EXTERNAL);
	const [tone, setTone] = useState(bible?.tone ?? "");
	const [setting, setSetting] = useState(bible?.setting ?? "");
	const [povKitNotes, setPovKitNotes] = useState(bible?.povKitNotes ?? "");
	const [busy, setBusy] = useState(false);

	const save = async (): Promise<void> => {
		setBusy(true);
		try {
			await onSave({ visualStyle, narrativeMode, tone, setting, povKitNotes: povKitNotes.trim() === "" ? null : povKitNotes });
		} finally {
			setBusy(false);
		}
	};

	const toggleLock = async (): Promise<void> => {
		setBusy(true);
		try {
			await onToggleLock(!locked);
		} finally {
			setBusy(false);
		}
	};

	return (
		<div className="flex flex-col gap-4">
			<div className="flex items-center justify-between">
				<Pill tone={locked ? "accent" : "neutral"}>{locked ? "Bible verrouillée" : "Bible modifiable"}</Pill>
				{bible ? (
					<Button size="sm" variant="ghost" isDisabled={busy} onPress={() => void toggleLock()}>
						{locked ? "Déverrouiller" : "Verrouiller"}
					</Button>
				) : null}
			</div>
			<div className="grid gap-4 md:grid-cols-2">
				<SelectField
					label="Style visuel"
					options={VISUAL_STYLE_OPTIONS}
					value={visualStyle}
					isDisabled={locked}
					onChange={(value) => setVisualStyle(narrowEnum(VISUAL_STYLE_VALUES, value, VisualStyle.CINEMATIC))}
				/>
				<SelectField
					label="Mode narratif"
					options={NARRATIVE_MODE_OPTIONS}
					value={narrativeMode}
					isDisabled={locked}
					onChange={(value) => setNarrativeMode(narrowEnum(NARRATIVE_MODE_VALUES, value, NarrativeMode.EXTERNAL))}
				/>
			</div>
			<TextField value={tone} onChange={setTone} isDisabled={locked}>
				<Label>Ton</Label>
				<Input placeholder="Sombre, tendu, mélancolique…" />
			</TextField>
			<TextField value={setting} onChange={setSetting} isDisabled={locked}>
				<Label>Univers / décor</Label>
				<TextArea placeholder="Néo-Paris, 2087, pluie permanente…" rows={2} />
			</TextField>
			<TextField value={povKitNotes} onChange={setPovKitNotes} isDisabled={locked}>
				<Label>Notes du kit POV (optionnel)</Label>
				<TextArea placeholder="Mains gantées, montre au poignet gauche…" rows={2} />
			</TextField>
			<div className="flex justify-end">
				<Button isDisabled={locked || busy} onPress={() => void save()}>
					{busy ? "Enregistrement…" : "Enregistrer la bible"}
				</Button>
			</div>
		</div>
	);
};

export default BibleForm;
